/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

// —— START: Progress-bar logic for Cart Drawer ——

const MARKET_CONFIG = {
  DK: {
    freeShipping: 699,
    locale: 'da-DK',
    currency: 'DKK',
    below(rem) {
      return `Du får gratis fragt, hvis du køber for ${formatPrice(rem, this.locale, this.currency)} mere`;
    },
    reached: 'Tillykke, du har opnået gratis fragt! ⚡️',
    error: 'Der opstod en fejl – prøv igen senere.',
    mobileFrom: 'køber ',
    mobileTo: 'køber<br>'
  },
  SE: {
    freeShipping: 999,
    locale: 'sv-SE',
    currency: 'SEK',
    below(rem) {
      return `Du får fri frakt om du handlar för ${formatPrice(rem, this.locale, this.currency)} till`;
    },
    reached: 'Grattis, du har fått fri frakt! ⚡️',
    error: 'Något gick fel – försök igen senare.',
    mobileFrom: 'handlar ',
    mobileTo: 'handlar<br>'
  },
  NL: {
    freeShipping: 69,
    locale: 'nl-NL',
    currency: 'EUR',
    below(rem) {
      return `Je krijgt gratis verzending als je nog voor ${formatPrice(rem, this.locale, this.currency)} koopt`;
    },
    reached: 'Gefeliciteerd, je hebt gratis verzending bereikt! ⚡️',
    error: 'Er is een fout opgetreden – probeer het later opnieuw.',
    mobileFrom: 'koopt',
    mobileTo: '<br>koopt'
  },
  BE: {
    freeShipping: 69,
    locale: 'nl-BE',
    currency: 'EUR',
    below(rem) {
      return `Je krijgt gratis verzending als je nog voor ${formatPrice(rem, this.locale, this.currency)} koopt`;
    },
    reached: 'Gefeliciteerd, je hebt gratis verzending bereikt! ⚡️',
    error: 'Er is een fout opgetreden – probeer het later opnieuw.',
    mobileFrom: 'koopt',
    mobileTo: '<br>koopt'
  },
  DE: {
    freeShipping: 69,
    locale: 'de-DE',
    currency: 'EUR',
    below(rem) {
      return `Du erhältst kostenlosen Versand, wenn du für ${formatPrice(rem, this.locale, this.currency)} mehr kaufst`;
    },
    reached: 'Glückwunsch, du hast kostenlosen Versand erhalten! ⚡️',
    error: 'Es ist ein Fehler aufgetreten – bitte versuche es später erneut.',
    mobileFrom: 'kaufst',
    mobileTo: '<br>kaufst'
  }
};

function getMarketCode() {
  const html = document.documentElement;
  const lang = (html.getAttribute('lang') || '').toLowerCase();
  const path = window.location.pathname.toLowerCase();
  const host = window.location.hostname.toLowerCase();

  if (lang === 'da' || lang === 'da-dk') return 'DK';
  if (lang === 'sv' || lang === 'sv-se') return 'SE';
  if (lang === 'nl' || lang === 'nl-nl') return 'NL';
  if (lang === 'nl-be') return 'BE';
  if (lang === 'de' || lang === 'de-de') return 'DE';

  if (path.startsWith('/sv') || path.startsWith('/sv-se') || path.startsWith('/se')) return 'SE';
  if (path.startsWith('/nl-be') || path.startsWith('/be')) return 'BE';
  if (path.startsWith('/nl') || path.startsWith('/nl-nl')) return 'NL';
  if (path.startsWith('/de') || path.startsWith('/de-de')) return 'DE';
  if (path.startsWith('/da') || path.startsWith('/da-dk') || path.startsWith('/dk')) return 'DK';

  if (host.endsWith('.dk')) return 'DK';
  if (host.endsWith('.se')) return 'SE';
  if (host.endsWith('.be')) return 'BE';
  if (host.endsWith('.de')) return 'DE';
  if (host.endsWith('.nl')) return 'NL';

  return null;
}

function getMarketConfig() {
  const marketCode = getMarketCode();
  return marketCode ? MARKET_CONFIG[marketCode] || null : null;
}

function formatPrice(val, locale, currency) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(val) ? 0 : 2,
    maximumFractionDigits: 2
  }).format(val);
}

function formatMobileLineBreak(config) {
  if (window.innerWidth > 749) return;

  const msgEl = document.getElementById('step-message');
  if (!msgEl || !config) return;

  msgEl.innerHTML = msgEl.textContent.replace(
    config.mobileFrom,
    config.mobileTo
  );
}

function updateProgress() {
  const fillEl = document.getElementById('progress-fill');
  const msgEl = document.getElementById('step-message');
  const pillEl = document.querySelector('#cart-drawer .fs-pill');

  if (!fillEl || !msgEl || !pillEl) return;

  const market = getMarketConfig();

  if (!market) {
    pillEl.style.display = 'none';
    fillEl.style.width = '0%';
    msgEl.textContent = '';
    return;
  }

  pillEl.style.display = '';

  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      const subtotal = cart.total_price / 100;
      const pct = Math.min((subtotal / market.freeShipping) * 100, 100);
      fillEl.style.width = pct + '%';

      if (subtotal < market.freeShipping) {
        const rem = market.freeShipping - subtotal;
        msgEl.textContent = market.below(rem);
      } else {
        msgEl.textContent = market.reached;
      }

      formatMobileLineBreak(market);
    })
    .catch(() => {
      msgEl.textContent = market.error;
      fillEl.style.width = '0%';
    });
}

updateProgress();

document.addEventListener('on:cart:add', () => {
  setTimeout(updateProgress, 500);
});

document.addEventListener('on:line-item:change', () => {
  setTimeout(updateProgress, 500);
});

document.addEventListener('dispatch:cart-drawer:refresh', () => {
  setTimeout(updateProgress, 500);
});

const drawerForm = document.getElementById('cart-drawer-form');
if (drawerForm) {
  drawerForm.querySelectorAll('input[name^="updates"]').forEach(i => {
    i.addEventListener('change', () => {
      const data = new FormData(drawerForm);
      fetch('/cart/update.js', { method: 'POST', body: data })
        .then(() => updateProgress())
        .catch(() => setTimeout(updateProgress, 300));
    });
  });

  drawerForm.addEventListener('submit', () => setTimeout(updateProgress, 300));
}

['cart:open', 'drawer:opened', 'shopify:drawerOpened', 'cart:updated', 'cart:refresh']
  .forEach(evt => document.body.addEventListener(evt, updateProgress));
// —— END: Progress-bar logic for Cart Drawer ——
